import { useEffect, useMemo, useState } from "react";
import { articleChapterPath as pathOf } from "../domain/articleChapters";
import { normalizeWorkUnit, initialCompatibleArticleId } from "../domain/workUnits";
import { visibleLibraryVersions, libraryVersionsFor } from "../domain/libraryVersions";
import { librarySearchText, matchesLibrarySearch } from "../domain/librarySearch";
import { reinforcedConcreteWorkUnits } from "../domain/reinforcedConcrete";
import { restoreComposition } from "../domain/restoreComposition";
import Bc3Inspector from "./Bc3Inspector";
import Bc3ArticleSource from "./Bc3ArticleSource";
import ArticleTechnicalSheet from "./ArticleTechnicalSheet";
import ArticlePriceRevision from "./ArticlePriceRevision";
import { supabase } from "../lib/supabase";
import { collectPaginatedRows } from "../lib/paginatedRows";
import type { EngineEstimateTarget } from "./SaveParametricDraft";
import type { ArticleBase } from "./ArticleBasePicker";
import "./ArticleLibrary.css";

type LibraryArticle = ArticleBase & {
  work_item_id: string;
  work_items: { code: string; name: string; unit: string };
  metadata: ArticleBase["metadata"] & { source_code?: string; source_mode?: string };
};
type TreeNode = { name: string; children: TreeNode[]; articles: LibraryArticle[] };
const money = (value: number | null) => value === null ? "—" : value.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
function makeTree(articles: LibraryArticle[]): TreeNode {
  const root: TreeNode = { name: "Biblioteca", children: [], articles: [] };
  for (const article of articles) {
    let node = root;
    for (const name of pathOf(article)) {
      let child = node.children.find((item) => item.name === name);
      if (!child) { child = { name, children: [], articles: [] }; node.children.push(child); }
      node = child;
    }
    node.articles.push(article);
  }
  return root;
}
function branchKeys(node: TreeNode, path: string[] = []): string[] {
  return node.children.flatMap(child => {
    const next=[...path,child.name];
    return [JSON.stringify(next),...branchKeys(child,next)];
  });
}
function branchCount(node: TreeNode): number {
  return node.articles.length+node.children.reduce((total,child)=>total+branchCount(child),0);
}
function Branch({ node, path, expanded, onToggle, selected, onSelect }: {
  node: TreeNode; path: string[]; expanded: Set<string>; onToggle:(key:string,open:boolean)=>void;
  selected: string | null; onSelect: (id: string) => void;
}) {
  const key=JSON.stringify(path);
  return <details open={expanded.has(key)} className="library-branch"
    onToggle={event=>onToggle(key,event.currentTarget.open)}>
    <summary>{node.name} <small className="library-branch-count">{branchCount(node)}</small></summary>
    <div>{node.children.map(child=><Branch key={child.name} node={child} path={[...path,child.name]}
      expanded={expanded} onToggle={onToggle} selected={selected} onSelect={onSelect} />)}
      {node.articles.map(article=><button type="button" key={article.id}
        className={selected===article.id ? "library-leaf active" : "library-leaf"}
        aria-pressed={selected===article.id} onClick={()=>onSelect(article.id)}>
        <small>{article.metadata.source_code ?? article.work_items.code} · {article.work_items.unit} · v{article.version}</small>
        <small>{article.review_status==="reviewed" ? "Revista" : "Por rever"}{article.metadata.price_review ? " · Preços de "+article.metadata.price_review.price_date : ""}</small>
        <span>{article.name}</span>
      </button>)}
    </div>
  </details>;
}
export default function ArticleLibrary({ onConfigure, target, onReturn, canEdit, onDirtyChange }: { onDirtyChange?:(dirty:boolean)=>void; canEdit: boolean; target: EngineEstimateTarget | null; onReturn: () => void; onConfigure: (base: ArticleBase, workCode: string) => void }) {
  const [sheetDirty,setSheetDirty]=useState(false);
  const [priceDirty,setPriceDirty]=useState(false);
  const libraryDirty=sheetDirty || priceDirty;
  useEffect(()=>{onDirtyChange?.(libraryDirty);return ()=>onDirtyChange?.(false);},[libraryDirty,onDirtyChange]);
  function mayLeave(){return !libraryDirty || window.confirm("Tem alterações na ficha ou uma revisão de preços ainda não guardada. Quer sair desta ficha e descartá-las?");}
  function returnSafely(){if(mayLeave())onReturn();}
  function selectSafely(id:string){if(id===selectedId || mayLeave())setSelectedId(id);}
  const [savedCalculation,setSavedCalculation] = useState<{id:string;created_at:string;metadata:unknown}|null>(null);
  const [restoreNotice,setRestoreNotice] = useState("");
  const [articles, setArticles] = useState<LibraryArticle[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showPrevious,setShowPrevious]=useState(false);
  const [reviewFilter,setReviewFilter]=useState<"all"|"reviewed"|"pending">("all");
  const [treeOpen, setTreeOpen] = useState(true);
  const [expanded,setExpanded]=useState<Set<string>>(()=>new Set());
  const [compatibleOnly,setCompatibleOnly]=useState(true);
  function toggleBranch(key:string,open:boolean){
    setExpanded(current=>{
      if(current.has(key)===open)return current;
      const next=new Set(current);if(open)next.add(key);else next.delete(key);return next;
    });
  }
  const [busy, setBusy] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setBusy(true);setError("");setRestoreNotice("");setSavedCalculation(null);setSelectedId(null);
    async function savedVersion() {
      if (!target) return null;
      const {data:item,error:itemError}=await supabase.from("estimate_items").select("selected_calculation_id").eq("id",target.itemId).maybeSingle();
      if(itemError) throw itemError;
      if(!item) throw new Error("O artigo da proposta já não está disponível.");
      if(!item.selected_calculation_id) return null;
      const {data,error:problem}=await supabase.from("estimate_item_calculations")
        .select("id,created_at,metadata").eq("id",item.selected_calculation_id).eq("estimate_item_id",target.itemId).maybeSingle();
      if(problem) throw problem;
      if(!data) throw new Error("Não foi possível recuperar o cálculo aplicado a este artigo. Consulte o histórico da proposta.");
      return data?.metadata?.kind==="reviewed_article_composition_v1"
        ? data as {id:string;created_at:string;metadata:unknown} : null;
    }
    void Promise.all([
      collectPaginatedRows<LibraryArticle>((from,to)=>supabase.from("parametric_article_bases")
        .select("*,work_items!inner(code,name,unit)").neq("review_status","archived")
        .order("name").order("version",{ascending:false}).order("id").range(from,to)),
      savedVersion(),
    ]).then(([catalog,saved])=>{
      if(cancelled) return;
      if(catalog.error) throw catalog.error;
      const rows=catalog.data ?? [];
      setArticles(rows);setSavedCalculation(saved);
      if(saved) {
        const metadata=saved.metadata as {article_base?:{id?:string}};
        const candidate=rows.find(article=>article.id===metadata.article_base?.id);
        const restored=candidate ? restoreComposition(saved.metadata,candidate) : null;
        if(candidate && restored?.data) setSelectedId(candidate.id);
        else {
          setSelectedId(null);
          setRestoreNotice(restored?.reason ?? "A ficha de origem está indisponível ou arquivada. Consulte a versão guardada no histórico da proposta antes de escolher outra ficha.");
        }
      } else setSelectedId(initialCompatibleArticleId(rows,target?.unit));
    }).catch(cause=>{if(!cancelled)setError(String(cause.message ?? cause));})
      .finally(()=>{if(!cancelled)setBusy(false);});
    return ()=>{cancelled=true;};
  }, [target?.itemId, loadAttempt]);
  async function refreshCatalog(){
    const {data,error:problem}=await collectPaginatedRows<LibraryArticle>((from,to)=>supabase.from("parametric_article_bases")
      .select("*,work_items!inner(code,name,unit)").neq("review_status","archived")
      .order("name").order("version",{ascending:false}).order("id").range(from,to));
    if(problem)throw problem;
    setArticles(data ?? []);
  }
  const searchIndex=useMemo(()=>articles.map(article=>{
    const model=reinforcedConcreteWorkUnits.find(item=>item.code===article.work_items.code);
    const options=Object.entries(article.parameter_values).flatMap(([key,value])=>[
      value,model?.fields.find(field=>field.key===key)?.options?.find(option=>option.value===value)?.label,
    ]);
    return {article,text:librarySearchText([
      article.name,article.work_items.name,article.work_items.code,article.metadata.source_code,
      ...pathOf(article),article.metadata.scope_notes,article.metadata.source_height_condition,...options,
      ...(article.metadata.configuration_facts ?? []).flatMap(fact=>[fact.label,fact.value]),
      ...(article.metadata.technical_sections ?? []).flatMap(section=>[section.title,section.text]),
      ...(article.metadata.reference_lines ?? []).flatMap(line=>[line.code,line.label,line.unit]),
      ...(article.metadata.bc3_import?.auxiliary_compositions ?? []).flatMap(node=>[node.code,node.label,node.unit,node.description,...node.resources.flatMap(line=>[line.code,line.label,line.unit])]),
    ])};
  }),[articles]);
  const visibleIds=useMemo(()=>new Set(visibleLibraryVersions(articles,showPrevious,selectedId).map(article=>article.id)),[articles,showPrevious,selectedId]);
  const filtered=useMemo(()=>searchIndex.filter(item=>visibleIds.has(item.article.id) && (!target || !compatibleOnly || normalizeWorkUnit(item.article.work_items.unit)===normalizeWorkUnit(target.unit)) && matchesLibrarySearch(item.text,query) && (reviewFilter==="all" || item.article.review_status===reviewFilter)).map(item=>item.article),
    [searchIndex,query,reviewFilter,visibleIds,target?.unit,compatibleOnly]);
  const tree = useMemo(() => makeTree(filtered), [filtered]);
  const selected = articles.find((article) => article.id === selectedId) ?? null;
  const filteredFichaCount=new Set(filtered.map(article=>JSON.stringify([article.work_item_id,article.name]))).size;
  useEffect(()=>{
    if(query.trim()){setExpanded(new Set(branchKeys(tree)));return;}
    if(!selected || !filtered.some(article=>article.id===selected.id))return;
    const path=pathOf(selected);
    setExpanded(current=>{
      const next=new Set(current);
      path.forEach((_,index)=>next.add(JSON.stringify(path.slice(0,index+1))));
      return next;
    });
  },[selectedId,tree,query]);
  const versions=useMemo(()=>selected ? libraryVersionsFor(articles,selected) : [],[articles,selected]);
  const latestVersion=versions[0] ?? null;
  const restored=selected && savedCalculation ? restoreComposition(savedCalculation.metadata,selected).data : null;
  return <section className="stack-xl">
    <header className="page-header"><div><p className="eyebrow">L37 Preços</p><h2>Biblioteca de artigos</h2>
      <p className="muted">Escolha um artigo para consultar a ficha e a composição.</p></div></header>
    {target ? <section className="card library-section">
      <p className="eyebrow">Consulta para a proposta · {target.estimateName}</p>
      <p><strong>{target.code}</strong> · {target.unit}</p><p>{target.description}</p>
      <p className="muted">Consultar uma ficha não altera este artigo. Reveja a composição para aplicar o custo a este artigo.</p>
      <button className="button button-secondary" type="button" onClick={returnSafely}>Voltar à proposta</button>
    </section> : null}
    {restoreNotice ? <div className="alert alert-error">{restoreNotice}</div> : null}
    {restored && savedCalculation ? <div className="alert alert-success">
      Versão aplicada à proposta recuperada · {new Date(savedCalculation.created_at).toLocaleString("pt-PT")}.
      Configuração, consumos e preços guardados. Uma nova aplicação conserva a versão anterior no histórico.
    </div> : null}
    {canEdit ? <Bc3Inspector onImported={refreshCatalog} /> : null}
    <div className="library-toolbar">
      <button className="button button-secondary" type="button" aria-expanded={treeOpen} aria-controls="article-tree"
        onClick={() => setTreeOpen((current) => !current)}>{treeOpen ? "Recolher árvore" : "Abrir árvore"}</button>
      <label className="library-search"><span>Pesquisar artigo, material, recurso ou capítulo</span>
        <input type="search" value={query} onChange={(event) => { setQuery(event.target.value); setTreeOpen(true); }} placeholder="Ex.: pilar A400, descofrante, EHS010" /></label>
      <fieldset className="library-review-filter"><legend>Estado da revisão</legend><div className="engine-choice-buttons">
        {([{value:"all",label:"Todas"},{value:"reviewed",label:"Revistas"},{value:"pending",label:"Por rever"}] as const).map(option=><button
          key={option.value} type="button" className={reviewFilter===option.value ? "button button-primary" : "button button-secondary"}
          aria-pressed={reviewFilter===option.value} onClick={()=>{setReviewFilter(option.value);setTreeOpen(true);}}>{option.label}</button>)}
      </div></fieldset>
      <button type="button" className="button button-secondary" aria-pressed={showPrevious} onClick={()=>{setShowPrevious(value=>!value);setTreeOpen(true);}}>{showPrevious ? "Ocultar versões anteriores" : "Mostrar versões anteriores"}</button>
      {target ? <button type="button" className={compatibleOnly ? "button button-primary" : "button button-secondary"}
        aria-pressed={compatibleOnly} onClick={()=>setCompatibleOnly(value=>!value)}>
        {compatibleOnly ? "Só fichas com unidade "+target.unit : "Todas as unidades"}
      </button> : null}
      {query || reviewFilter!=="all" || showPrevious || !compatibleOnly ? <button type="button" className="button button-secondary"
        onClick={()=>{setQuery("");setReviewFilter("all");setShowPrevious(false);setCompatibleOnly(true);setExpanded(new Set());setTreeOpen(true);}}>Repor filtros</button> : null}
      <span className="muted">{filteredFichaCount} fichas · {filtered.length} versões na árvore</span>
    </div>
    {!busy && !error && selected && !filtered.some(article=>article.id===selected.id)
      ? <p className="alert" role="status">A ficha aberta não faz parte dos resultados atuais. Pode alterar os filtros ou escolher outra ficha na árvore; as alterações nesta ficha continuam preservadas.</p> : null}
    {busy ? <p role="status">A carregar biblioteca…</p> : null}
    {error ? <div className="alert alert-error" role="alert"><p>Não foi possível carregar a biblioteca: {error}</p>
      <button type="button" className="button button-secondary" disabled={busy} onClick={()=>setLoadAttempt(attempt=>attempt+1)}>Tentar carregar novamente</button>
    </div> : null}
    {!busy && !error && !articles.length ? <p>A biblioteca ainda não tem artigos importados.</p> : null}
    <div className={treeOpen ? "library-layout" : "library-layout collapsed"}>
      {treeOpen ? <aside className="card library-tree" id="article-tree" aria-label="Capítulos e artigos">
        <div className="library-tree-actions">
          <button type="button" className="button button-secondary" onClick={()=>setExpanded(new Set(branchKeys(tree)))}>Abrir capítulos</button>
          <button type="button" className="button button-secondary" onClick={()=>setExpanded(new Set())}>Fechar capítulos</button>
        </div>
        {tree.children.map(node=><Branch key={node.name} node={node} path={[node.name]}
          expanded={expanded} onToggle={toggleBranch} selected={selectedId} onSelect={selectSafely} />)}
        {!busy && !error && !filtered.length ? <p>Nenhum artigo encontrado.</p> : null}
      </aside> : null}
      <div className="library-sheet">
        {selected ? <>
          <section className="card library-heading"><div><p className="muted">{pathOf(selected).join(" › ")}</p>
            <p className="mono">{selected.metadata.source_code ?? selected.work_items.code} · v{selected.version}</p>
            <h3>{selected.name}</h3><p>{selected.work_items.name}</p></div>
            <div><span>Preço da configuração de origem</span><strong>{money(selected.reference_unit_price)} / {selected.work_items.unit}</strong>
              <small>{selected.review_status === "reviewed" ? "Revista" : "Por rever"} · {selected.metadata.bc3_import ? "BC3" : selected.source_kind === "cype_reference" ? "CYPE" : "Interna"}</small></div>
          </section>
          {versions.length>1 ? <section className="card library-section" aria-label="Versões desta ficha">
            <h3>Versões desta ficha</h3>
            {latestVersion?.id!==selected.id ? <p className="technical-invalid">Está a consultar a versão v{selected.version}. Existe uma versão mais recente: v{latestVersion?.version}. Escolher outra versão abre a ficha para revisão; o custo da proposta só muda quando o aplicar.</p>
              : <p className="muted">Está a consultar a versão mais recente disponível. As versões anteriores são conservadas.</p>}
            <details><summary>Consultar {versions.length} versões disponíveis</summary>
              <div className="library-version-list">{versions.map(version=><button key={version.id} type="button"
                className={version.id===selected.id ? "button button-primary" : "button button-secondary"}
                aria-pressed={version.id===selected.id} onClick={()=>selectSafely(version.id)}>
                <strong>v{version.version}{version.id===latestVersion?.id ? " · Mais recente" : ""}</strong>
                <span>{money(version.reference_unit_price)} / {version.work_items.unit} · {version.review_status==="reviewed" ? "Revista" : "Por rever"}</span>
                {version.metadata.price_review ? <span>Preços de {version.metadata.price_review.price_date}</span> : null}
              </button>)}</div>
            </details>
          </section> : null}
          <section className="card library-section" aria-label="Origem e condições da ficha">
            <h3>Origem e condições da ficha</h3>
            <p>{selected.metadata.bc3_import ? "Importação BC3" : selected.source_kind==="cype_reference" ? "Referência CYPE" : "Referência interna"} · Versão {selected.version}
              {" · "+(selected.review_status==="reviewed" ? "Revista" : "Por rever")}</p>
            <p>{selected.metadata.bc3_import ? "Data de importação:" : "Data de consulta da origem:"} <strong>{selected.source_observed_at && Number.isFinite(Date.parse(selected.source_observed_at))
              ? new Date(selected.source_observed_at).toLocaleDateString("pt-PT") : "Não registada"}</strong></p>
            <p className="muted">A data de consulta identifica a referência recolhida. Confirme os preços atuais dos recursos antes de os aplicar à obra.</p>
            {selected.metadata.source_height_condition ? <p><strong>Condição de execução:</strong> {selected.metadata.source_height_condition}</p> : null}
            {selected.metadata.scope_notes ? <p className="library-scope-notes">{selected.metadata.scope_notes}</p>
              : <p className="muted">Condições de utilização ainda não registadas nesta ficha.</p>}
            {selected.source_url && /^https?:\/\//i.test(selected.source_url)
              ? <a href={selected.source_url} target="_blank" rel="noopener noreferrer">Consultar ficha na origem</a> : null}
            {selected.review_status!=="reviewed" ? <p className="technical-invalid">Esta referência está por rever. A configuração e a composição precisam de confirmação para cada obra.</p> : null}
          </section>
          {selected.metadata.bc3_import ? <Bc3ArticleSource key={selected.metadata.bc3_import.batch_id} source={selected.metadata.bc3_import} /> : null}
          {selected.metadata.price_review ? <section className="alert" aria-label="Revisão de preços desta versão">
            <strong>Preços revistos internamente</strong>
            <p>Data dos preços: {selected.metadata.price_review.price_date} · Guardados em {new Date(selected.metadata.price_review.reviewed_at).toLocaleString("pt-PT")}</p>
            <p className="library-scope-notes">{selected.metadata.price_review.note}</p>
            <p className="muted">A composição técnica continua sujeita a revisão para a obra.</p>
          </section> : null}
          {canEdit ? <ArticlePriceRevision key={selected.id} base={selected} onSaved={refreshCatalog} onDirtyChange={setPriceDirty} /> : null}
          <ArticleTechnicalSheet key={selected.id+":"+(restored ? savedCalculation?.id ?? "" : "origin")+":"+(target?.itemId ?? "")} base={selected} workCode={selected.work_items.code} unit={selected.work_items.unit}
            target={target} canEdit={canEdit} onReturn={returnSafely} onDirtyChange={setSheetDirty} restored={restored ?? undefined} />
          {target && normalizeWorkUnit(target.unit) !== normalizeWorkUnit(selected.work_items.unit)
            ? <div className="alert alert-error">A unidade desta ficha ({selected.work_items.unit}) difere da unidade do artigo ({target.unit}). Escolha uma ficha compatível.</div>
            : reinforcedConcreteWorkUnits.some(model=>model.code===selected.work_items.code) ? <button className="button button-secondary" type="button" onClick={() => {if(mayLeave())onConfigure(selected, selected.work_items.code);}}>Consultar motor técnico experimental</button> : null}
        </> : !busy && !error ? <p>Escolha um artigo na árvore.</p> : null}
      </div>
    </div>
  </section>;
}
